import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../services/api';
import { localDateStr } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import Modal from '../components/common/Modal';

const TABS = ['Overview', 'Receipts', 'Expenses', 'AR Aging', 'Commissions'];

const PAYMENT_METHODS = ['bank_transfer', 'cheque', 'cash', 'upi', 'wire_transfer', 'other'];
const EXPENSE_CATS = ['travel', 'office_supplies', 'utilities', 'rent', 'marketing', 'software', 'equipment', 'raw_material', 'salary', 'other'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP'];

const PIE_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#94a3b8'];

const fmt = (n, cur = 'INR') => {
  const num = Number(n) || 0;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(num);
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const toInput = (d) => d ? d.slice(0, 10) : '';
const catLabel = (c) => c ? c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '—';
const methodLabel = (m) => m ? m.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '—';

const ageLabel = (days) => {
  if (days <= 30)  return { label: '0–30 days',   color: '#10b981' };
  if (days <= 60)  return { label: '31–60 days',  color: '#f59e0b' };
  if (days <= 90)  return { label: '61–90 days',  color: '#f97316' };
  return                   { label: '90+ days',    color: '#ef4444' };
};

// ─── Stat Card ───────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, color = 'var(--primary)' }) => (
  <div className="card" style={{ flex: 1 }}>
    <div className="card-body" style={{ padding: '16px 20px' }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 800, color, lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  </div>
);

// ─── Payment Modal ────────────────────────────────────────────────────────────
function PaymentModal({ open, initial, onClose, onSaved }) {
  const { companyCurrency } = useCompany();
  const [form, setForm] = useState({ order_id: '', client_id: '', amount: '', currency: companyCurrency, payment_date: '', method: 'bank_transfer', reference: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get('/orders?order_type=invoice&limit=200').then(r => setOrders(r.data.data || [])).catch(() => {});
    api.get('/clients?limit=200&approval_status=approved').then(r => setClients(r.data.data || [])).catch(() => {});
    if (initial) {
      setForm({
        order_id:     initial.order_id     || '',
        client_id:    initial.client_id    || '',
        amount:       initial.amount       || '',
        currency:     initial.currency     || 'INR',
        payment_date: toInput(initial.payment_date),
        method:       initial.method       || 'bank_transfer',
        reference:    initial.reference    || '',
        notes:        initial.notes        || '',
      });
    } else {
      setForm({ order_id: '', client_id: '', amount: '', currency: companyCurrency, payment_date: localDateStr(), method: 'bank_transfer', reference: '', notes: '' });
    }
    setError('');
  }, [open, initial, companyCurrency]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.payment_date) { setError('Amount and date are required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, order_id: form.order_id || null, client_id: form.client_id || null };
      if (initial?.id) await api.put(`/accounting/payments/${initial.id}`, payload);
      else             await api.post('/accounting/payments', payload);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 520, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{initial?.id ? 'Edit Payment' : 'Record Payment'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Amount *</label>
                <input className="form-control" type="number" step="0.01" min="0" value={form.amount} onChange={e => set('amount', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Currency</label>
                <select className="form-control" value={form.currency} onChange={e => set('currency', e.target.value)}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Payment Date *</label>
                <input className="form-control" type="date" value={form.payment_date} onChange={e => set('payment_date', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Method</label>
                <select className="form-control" value={form.method} onChange={e => set('method', e.target.value)}>
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{methodLabel(m)}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Link to Invoice (optional)</label>
              <select className="form-control" value={form.order_id} onChange={e => set('order_id', e.target.value)}>
                <option value="">— None —</option>
                {orders.map(o => <option key={o.id} value={o.id}>{o.order_number} — {o.client_name} ({fmt(o.total, o.currency || 'INR')})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Client (if no invoice)</label>
              <select className="form-control" value={form.client_id} onChange={e => set('client_id', e.target.value)}>
                <option value="">— None —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Reference / Cheque No.</label>
              <input className="form-control" value={form.reference} onChange={e => set('reference', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Payment'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Expense Modal ─────────────────────────────────────────────────────────
function ExpenseModal({ open, initial, onClose, onSaved }) {
  const { companyCurrency } = useCompany();
  const [form, setForm] = useState({ category: 'other', description: '', amount: '', currency: companyCurrency, expense_date: '', vendor: '', reference: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        category:     initial.category     || 'other',
        description:  initial.description  || '',
        amount:       initial.amount       || '',
        currency:     initial.currency     || 'INR',
        expense_date: toInput(initial.expense_date),
        vendor:       initial.vendor       || '',
        reference:    initial.reference    || '',
        notes:        initial.notes        || '',
      });
    } else {
      setForm({ category: 'other', description: '', amount: '', currency: companyCurrency, expense_date: localDateStr(), vendor: '', reference: '', notes: '' });
    }
    setError('');
  }, [open, initial, companyCurrency]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount || !form.expense_date) { setError('Description, amount, and date are required.'); return; }
    setSaving(true); setError('');
    try {
      if (initial?.id) await api.put(`/accounting/expenses/${initial.id}`, form);
      else             await api.post('/accounting/expenses', form);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 520, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{initial?.id ? 'Edit Expense' : 'Record Expense'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-control" value={form.category} onChange={e => set('category', e.target.value)}>
                  {EXPENSE_CATS.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input className="form-control" type="date" value={form.expense_date} onChange={e => set('expense_date', e.target.value)} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description *</label>
              <input className="form-control" value={form.description} onChange={e => set('description', e.target.value)} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Amount *</label>
                <input className="form-control" type="number" step="0.01" min="0" value={form.amount} onChange={e => set('amount', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Currency</label>
                <select className="form-control" value={form.currency} onChange={e => set('currency', e.target.value)}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Vendor</label>
                <input className="form-control" value={form.vendor} onChange={e => set('vendor', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Reference</label>
                <input className="form-control" value={form.reference} onChange={e => set('reference', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Expense'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ displayCurrency, companyCurrency, usdToInrRate }) {
  const [stats, setStats]    = useState(null);
  const [chart, setChart]    = useState([]);
  const [pieData, setPieData] = useState([]);
  const [year, setYear]      = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  // Convert a value from companyCurrency to displayCurrency for UI presentation
  const toDisplay = (n) => {
    const v = Number(n) || 0;
    if (displayCurrency === companyCurrency) return v;
    if (companyCurrency === 'INR' && displayCurrency === 'USD') return v / usdToInrRate;
    if (companyCurrency === 'USD' && displayCurrency === 'INR') return v * usdToInrRate;
    return v;
  };
  const fmtD = (n) => fmt(toDisplay(n), displayCurrency);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, p] = await Promise.all([
        api.get('/accounting/overview'),
        api.get(`/accounting/monthly-chart?year=${year}`),
        api.get(`/accounting/expense-breakdown?year=${year}`),
      ]);
      setStats(s.data.data);
      setChart(c.data.data || []);
      setPieData((p.data.data || []).map((r, i) => ({ name: catLabel(r.category), value: Number(r.total), color: PIE_COLORS[i % PIE_COLORS.length] })));
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner spinner-lg" /></div>;

  const outstanding = stats?.outstanding || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Stat row */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <StatCard label="Total Invoiced" value={fmtD(stats?.total_invoiced)} sub={`This month: ${fmtD(stats?.invoiced_this_month)}`} color="var(--primary)" />
        <StatCard label="Total Received" value={fmtD(stats?.total_received)} sub={`This month: ${fmtD(stats?.received_this_month)}`} color="#10b981" />
        <StatCard label="Outstanding AR" value={fmtD(outstanding)} sub={outstanding > 0 ? 'Unpaid invoices' : 'All clear'} color={outstanding > 0 ? '#ef4444' : '#10b981'} />
        <StatCard label="Total Expenses" value={fmtD(stats?.total_expenses)} sub={`This month: ${fmtD(stats?.expenses_this_month)}`} color="#f59e0b" />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Monthly bar chart */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="card-title">Monthly Revenue vs Expenses</span>
            <select className="form-control" style={{ width: 90, padding: '4px 8px', fontSize: '0.8rem' }}
              value={year} onChange={e => setYear(Number(e.target.value))}>
              {[2023, 2024, 2025, 2026].map(y => <option key={y}>{y}</option>)}
            </select>
          </div>
          <div className="card-body" style={{ padding: '8px 16px 16px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chart} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip formatter={(v) => fmtD(v)} />
                <Legend />
                <Bar dataKey="revenue"  name="Revenue"  fill="var(--primary)" radius={[3,3,0,0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#f59e0b"       radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Expense breakdown pie */}
        <div className="card">
          <div className="card-header"><span className="card-title">Expense Breakdown ({year})</span></div>
          <div className="card-body" style={{ padding: '8px 16px 16px' }}>
            {pieData.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40, fontSize: '0.875rem' }}>No expense data for {year}</div>
            ) : (
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <ResponsiveContainer width="55%" height={220}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value">
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtD(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {pieData.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem' }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                      <span style={{ flex: 1, color: 'var(--text-secondary)' }}>{d.name}</span>
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{fmtD(d.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Receipts (Payments) Tab ──────────────────────────────────────────────────
function ReceiptsTab() {
  const { isPrivileged } = useAuth();
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [fromDate, setFrom]   = useState('');
  const [toDate, setTo]       = useState('');
  const [modal, setModal]     = useState({ open: false, item: null });
  const [freezeConfirm, setFreezeConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search)  params.set('search', search);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate)   params.set('to_date', toDate);
      const { data } = await api.get(`/accounting/payments?${params}`);
      setRows(data.data || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [page, search, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const handleFreeze = async () => {
    await api.delete(`/accounting/payments/${freezeConfirm.id}`);
    setFreezeConfirm(null);
    load();
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="form-control" placeholder="Search payment #, ref, client…" style={{ flex: 1, minWidth: 180 }}
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <input className="form-control" type="date" style={{ width: 150 }} value={fromDate} onChange={e => { setFrom(e.target.value); setPage(1); }} />
        <input className="form-control" type="date" style={{ width: 150 }} value={toDate} onChange={e => { setTo(e.target.value); setPage(1); }} />
        <button className="btn btn-primary" onClick={() => setModal({ open: true, item: null })}>+ Record Payment</button>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Payment #</th>
                <th>Date</th>
                <th>Client</th>
                <th>Invoice</th>
                <th>Method</th>
                <th>Reference</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32 }}><div className="spinner" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No payments found</td></tr>
              ) : rows.map(r => (
                <tr key={r.id}>
                  <td><span style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--primary)' }}>{r.payment_number}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.payment_date)}</td>
                  <td>{r.client_name || '—'}</td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{r.order_number || '—'}</td>
                  <td><span className="badge badge-neutral">{methodLabel(r.method)}</span></td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{r.reference || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.amount, r.currency)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-xs btn-secondary" title="Edit" onClick={() => setModal({ open: true, item: r })}>
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      {isPrivileged && (
                        <button className="btn btn-xs btn-danger" title="Freeze" onClick={() => setFreezeConfirm(r)}>
                          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.875rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>{total} payments</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-xs btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              <span style={{ padding: '4px 10px', background: 'var(--surface-2)', borderRadius: 6 }}>{page} / {totalPages}</span>
              <button className="btn btn-xs btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      <PaymentModal open={modal.open} initial={modal.item} onClose={() => setModal({ open: false, item: null })} onSaved={() => { setModal({ open: false, item: null }); load(); }} />
      <Modal open={!!freezeConfirm} onClose={() => setFreezeConfirm(null)} title="Freeze Payment"
        footer={<><button className="btn btn-secondary" onClick={() => setFreezeConfirm(null)}>Cancel</button><button className="btn btn-danger" onClick={handleFreeze}>Freeze</button></>}
      >
        <p>Freeze payment <strong>{freezeConfirm?.payment_number}</strong>? It will become read-only.</p>
      </Modal>
    </div>
  );
}

// ─── Expenses Tab ─────────────────────────────────────────────────────────────
function ExpensesTab() {
  const { isPrivileged } = useAuth();
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [category, setCat]    = useState('');
  const [fromDate, setFrom]   = useState('');
  const [toDate, setTo]       = useState('');
  const [modal, setModal]     = useState({ open: false, item: null });
  const [freezeConfirm, setFreezeConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search)   params.set('search', search);
      if (category) params.set('category', category);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate)   params.set('to_date', toDate);
      const { data } = await api.get(`/accounting/expenses?${params}`);
      setRows(data.data || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [page, search, category, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const handleFreeze = async () => {
    await api.delete(`/accounting/expenses/${freezeConfirm.id}`);
    setFreezeConfirm(null);
    load();
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="form-control" placeholder="Search description, vendor…" style={{ flex: 1, minWidth: 180 }}
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select className="form-control" style={{ width: 160 }} value={category} onChange={e => { setCat(e.target.value); setPage(1); }}>
          <option value="">All Categories</option>
          {EXPENSE_CATS.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
        </select>
        <input className="form-control" type="date" style={{ width: 150 }} value={fromDate} onChange={e => { setFrom(e.target.value); setPage(1); }} />
        <input className="form-control" type="date" style={{ width: 150 }} value={toDate} onChange={e => { setTo(e.target.value); setPage(1); }} />
        <button className="btn btn-primary" onClick={() => setModal({ open: true, item: null })}>+ Record Expense</button>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Expense #</th>
                <th>Date</th>
                <th>Category</th>
                <th>Description</th>
                <th>Vendor</th>
                <th>Recorded By</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32 }}><div className="spinner" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No expenses found</td></tr>
              ) : rows.map(r => (
                <tr key={r.id}>
                  <td><span style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--primary)' }}>{r.expense_number}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.expense_date)}</td>
                  <td><span className="badge badge-warning">{catLabel(r.category)}</span></td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{r.vendor || '—'}</td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{r.recorded_by_name || '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.amount, r.currency)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-xs btn-secondary" title="Edit" onClick={() => setModal({ open: true, item: r })}>
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      {isPrivileged && (
                        <button className="btn btn-xs btn-danger" title="Freeze" onClick={() => setFreezeConfirm(r)}>
                          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.875rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>{total} expenses</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-xs btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              <span style={{ padding: '4px 10px', background: 'var(--surface-2)', borderRadius: 6 }}>{page} / {totalPages}</span>
              <button className="btn btn-xs btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      <ExpenseModal open={modal.open} initial={modal.item} onClose={() => setModal({ open: false, item: null })} onSaved={() => { setModal({ open: false, item: null }); load(); }} />
      <Modal open={!!freezeConfirm} onClose={() => setFreezeConfirm(null)} title="Freeze Expense"
        footer={<><button className="btn btn-secondary" onClick={() => setFreezeConfirm(null)}>Cancel</button><button className="btn btn-danger" onClick={handleFreeze}>Freeze</button></>}
      >
        <p>Freeze expense <strong>{freezeConfirm?.expense_number}</strong>? It will become read-only.</p>
      </Modal>
    </div>
  );
}

// ─── AR Aging Tab ──────────────────────────────────────────────────────────────
function ArAgingTab() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/accounting/ar-aging')
      .then(r => setRows(r.data.data || []))
      .finally(() => setLoading(false));
  }, []);

  // Group by bucket
  const buckets = { '0–30 days': [], '31–60 days': [], '61–90 days': [], '90+ days': [] };
  rows.forEach(r => { const { label } = ageLabel(r.age_days); buckets[label]?.push(r); });

  const bucketTotals = Object.entries(buckets).map(([label, items]) => ({
    label,
    count: items.length,
    total: items.reduce((s, r) => s + r.outstanding, 0),
    color: ageLabel(items[0]?.age_days ?? 0).color,
  }));

  const grandTotal = rows.reduce((s, r) => s + r.outstanding, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Summary buckets */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {bucketTotals.map(b => (
          <StatCard key={b.label} label={b.label} value={fmt(b.total)} sub={`${b.count} invoice${b.count !== 1 ? 's' : ''}`} color={b.color} />
        ))}
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="card-title">Outstanding Invoices</span>
          <span style={{ fontSize: '0.875rem', fontWeight: 700, color: grandTotal > 0 ? 'var(--error)' : '#10b981' }}>
            Total: {fmt(grandTotal)}
          </span>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Client</th>
                <th>Date</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Paid</th>
                <th style={{ textAlign: 'right' }}>Outstanding</th>
                <th>Age</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32 }}><div className="spinner" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No outstanding invoices</td></tr>
              ) : rows.map(r => {
                const { label, color } = ageLabel(r.age_days);
                return (
                  <tr key={r.id}>
                    <td><span style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: 'var(--primary)' }}>{r.order_number}</span></td>
                    <td>{r.client_name}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(r.total, r.currency)}</td>
                    <td style={{ textAlign: 'right', color: '#10b981' }}>{fmt(r.paid, r.currency)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#ef4444' }}>{fmt(r.outstanding, r.currency)}</td>
                    <td>
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color, padding: '2px 8px', background: `${color}18`, borderRadius: 4 }}>
                        {r.age_days}d — {label}
                      </span>
                    </td>
                    <td><span className={`badge badge-${r.status === 'delivered' ? 'success' : r.status === 'processing' ? 'primary' : 'neutral'}`} style={{ textTransform: 'capitalize' }}>{r.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Commissions Tab ──────────────────────────────────────────────────────────
function CommissionsTab() {
  const curYear = new Date().getFullYear();
  const [year, setYear]         = useState(String(curYear));
  const [month, setMonth]       = useState('');
  const [data, setData]         = useState([]);
  const [chart, setChart]       = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (month) params.set('month', `${year}-${month}`);
      else params.set('year', year);
      const { data: res } = await api.get(`/accounting/commission-report?${params}`);
      setData(res.data || []);
      setChart(res.chart || []);
      setSummary(res.summary || null);
    } finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const roleColor = (role) => role === 'admin' ? '#6366f1' : role === 'manager' ? '#3b82f6' : '#10b981';

  return (
    <div>
      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Year</label>
          <select className="form-control" style={{ width: 100 }} value={year} onChange={e => { setYear(e.target.value); setMonth(''); }}>
            {[curYear, curYear - 1, curYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Month</label>
          <select className="form-control" style={{ width: 130 }} value={month} onChange={e => setMonth(e.target.value)}>
            <option value="">All months</option>
            {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, i) => (
              <option key={m} value={m}>{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i]}</option>
            ))}
          </select>
          <button className="btn btn-primary btn-sm" onClick={load}>Apply</button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 }}>
          {[
            { label: 'Total Commission', value: fmt(summary.total_commission), color: '#6366f1' },
            { label: 'Total Revenue',    value: fmt(summary.total_value),      color: '#0f172a' },
            { label: 'Orders',           value: summary.order_count,            color: '#3b82f6' },
            { label: 'Reps with Commission', value: summary.mr_count,          color: '#10b981' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>{s.label}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Monthly chart */}
      {chart.some(c => c.commission > 0) && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Monthly Commission — {year}</span></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chart} margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => fmt(v)} />
                <Bar dataKey="commission" name="Commission" fill="#6366f1" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Per-MR table */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="card-title">By Sales Representative</span>
          <button className="btn btn-secondary btn-sm no-print" onClick={() => window.print()}>
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Print
          </button>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th style={{ textAlign: 'center' }}>Orders</th>
                <th style={{ textAlign: 'right' }}>Revenue Generated</th>
                <th style={{ textAlign: 'right' }}>Commission Earned</th>
                <th style={{ textAlign: 'right' }}>Comm %</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32 }}><div className="spinner" /></td></tr>}
              {!loading && data.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>No commission data for this period</td></tr>
              )}
              {!loading && data.map(r => {
                const commPct = r.total_value > 0 ? (r.total_commission / r.total_value * 100).toFixed(1) : '0.0';
                return (
                  <tr key={r.user_id}>
                    <td style={{ fontWeight: 700 }}>{r.user_name}</td>
                    <td><span style={{ padding: '2px 10px', borderRadius: 12, background: `${roleColor(r.role)}18`, color: roleColor(r.role), fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize' }}>{r.role}</span></td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{r.order_count}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.total_value)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: '#6366f1' }}>{fmt(r.total_commission)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{commPct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Main Accounting Page ─────────────────────────────────────────────────────
export default function Accounting() {
  const [tab, setTab] = useState(0);
  const { companyCurrency, usdToInrRate } = useCompany();
  const [displayCurrency, setDisplayCurrency] = useState(companyCurrency);

  // Sync display currency when company setting loads
  useEffect(() => {
    setDisplayCurrency(companyCurrency);
  }, [companyCurrency]);

  const altCurrency = companyCurrency === 'INR' ? 'USD' : 'INR';

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Accounting</h1>
          <p className="page-subtitle">Track payments, expenses, and accounts receivable</p>
        </div>
        {/* Display currency toggle — only visible on Overview tab */}
        {tab === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>View in:</span>
            <div style={{ display: 'flex', background: 'var(--surface-2, #f1f5f9)', borderRadius: 8, padding: 3, gap: 2 }}>
              {[companyCurrency, altCurrency].map(c => (
                <button key={c} onClick={() => setDisplayCurrency(c)} style={{
                  padding: '4px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  fontSize: '0.8rem', fontWeight: 700,
                  background: displayCurrency === c ? '#fff' : 'transparent',
                  color: displayCurrency === c ? 'var(--text-primary)' : 'var(--text-muted)',
                  boxShadow: displayCurrency === c ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s',
                }}>{c}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '0.875rem', fontWeight: tab === i ? 700 : 500,
            color: tab === i ? 'var(--primary)' : 'var(--text-secondary)',
            borderBottom: tab === i ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -1, transition: 'all 0.15s',
          }}>{t}</button>
        ))}
      </div>

      {tab === 0 && <OverviewTab displayCurrency={displayCurrency} companyCurrency={companyCurrency} usdToInrRate={usdToInrRate} />}
      {tab === 1 && <ReceiptsTab />}
      {tab === 2 && <ExpensesTab />}
      {tab === 3 && <ArAgingTab />}
      {tab === 4 && <CommissionsTab />}
    </div>
  );
}
